import React, { useEffect, useMemo, useState } from 'react';
import authToken from 'src/utils/auth';
import { useTranslation } from 'react-i18next';
import { EiqButton, EiqSpinner } from 'eiq-storybook';
import { saveAs } from 'file-saver';
import { compact } from 'lodash';

import { CapBoxDefaultProps, CapBoxPropTypes } from 'src/components/CapBoxNew/propTypes';
import { CapFinding } from 'src/components/CapFindingNew/CapFinding';
import { flatToTree, sectionsWithNumbersFlat } from 'src/utils/form/section';
import { ServiceType } from 'src/components/CapBoxNew/constants';
import { downloadAttachments } from 'src/utils/helpers';
import { withJobContext } from 'src/utils/context/JobContext';
import Table from 'src/components/Table';
import { SurveyType, CompanyType } from 'src/utils/data/types';
import { toSimpleUtcDateString } from 'src/utils/format/date';
import { useCapHistories } from 'src/hooks/cap';
import { useDeleteJobExternalStakeholder } from 'src/hooks/jobExternalStakeholder/useDeleteJobExternalStakeholder';
import { useExternalJobStakeholders } from 'src/hooks/jobExternalStakeholder/useJobExternalStakeholders';
import Loader from 'src/components/Loader';
import logger from 'src/utils/logger';
import s from 'src/components/CapBoxNew/Capbox.styles';
import useGetJobChecklists from 'src/components/JobMobileDataTab/hooks/useGetJobChecklists';
import client from 'src/utils/api/client';
import { EXPORT_CAP_NEW } from 'src/graphql/cap/mutation';
import { notification } from 'src/components/Notification';
import { getAffiliationOptionsList } from 'src/components/JobForm/utils';
import { getQuestionNumber } from 'src/components/ChecklistPreviewForm/utils';
import { STATE_PROVINCE_QUERY } from 'src/graphql/stateProvince/query';
import SiteRepresentative from 'src/components/SiteRepresentative';
import SiteRepresentativeInvitePopup from 'src/components/SiteRepresentativeInvitePopup/SiteRepresentativeInvitePopup';
import { wasCapSaved, shouldDisableClientIdInput } from './utils';
import { CapboxHeader } from './CapBoxHeaderNew/CapboxHeadernew';
import { CapBoxPageHeader } from './CapboxPageHeader/CapBoxPageHeader';
import { CapBoxAssessmentInformation } from './CapboxAssessmentInformation/CapBoxAssessmentInformation';
import CapboxAssessmentResult from './CapBoxAssessmentResult/CapboxAssessmentResult';
import CapboxDigitalLearning from './CapboxDigitalLearning/CapboxDigitalLearning';
import CapboxProgress from './CapBoxProgress/CapboxProgress';
import CapboxKeyMilestoneDates from './CapboxKeyMilestoneDates/CapboxKeyMilestoneDates';

const CapBoxNew = ({
  auth,
  capFindingsData,
  capFindingsLoading,
  jobChecklists,
  companyTypes,
  country,
  job,
  jobContext,
  jobStatus,
  jobOverallCapStatus: overallCapStatusValue,
  reloadCapFindings,
  values,
  setFieldValue,
  handleSubmit,
  errors,
  setClientId,
  clientId,
  isCapSubmitting,
  redirect,
}) => {
  const authenticated = authToken();
  const currentUserId = authenticated?.user?.id ? parseInt(authenticated.user.id, 10) : null;
  const { id: jobId } = job;
  const { i18n, t } = useTranslation();
  const { checklists, loading: loadingChecklists, getChecklists } = useGetJobChecklists(jobId);
  const [
    getCapHistories,
    { data: capHistoriesData, loading: capHistoriesLoading, refetch: refetchCapHistories },
  ] = useCapHistories({});

  const [isLoadingJobExternalStakeholders, setIsLoadingJobExternalStakeholders] = useState(false);
  const [triggerRefetchJobExternalStakeholders, setTriggerRefetchJobExternalStakeholders] = useState(0);
  const [jobExternalStakeholders, setJobExternalStakeholders] = useState([]);
  const [infoCollapsed, setInfoCollapsed] = useState(true);
  const [showInvitePopup, setShowInvitePopup] = useState(null);
  const [factoryAddresses, setFactoryAddresses] = useState('');
  const [loading, setLoading] = useState(isCapSubmitting || capFindingsLoading);
  const [exportLoading, setExportLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { jobStakeholders } = jobContext;
  const capManagerJobStakeholders = jobStakeholders.filter((stk) => stk.role === "CAP_Manager");

  const allExternalUsersId = jobExternalStakeholders?.map(user => user.userId);
  const externalUserResult = allExternalUsersId.length >= 1 ? allExternalUsersId.includes(currentUserId) : false;

  const isExternalUser = (auth.isFactoryUser || auth.isVendorUser) && externalUserResult;
  const isCAPManager = auth.isCapManager;
  const isEditable = isExternalUser || isCAPManager || auth.isAdmin;
  const editableForExternalUser = (isExternalUser || auth.isAdmin);
  const hasError = Object.keys(errors).length !== 0;
  const disableClickForCourse = !(isCAPManager || auth.isAdmin) ? !isExternalUser : false;
  const disableClickForClient = shouldDisableClientIdInput(isExternalUser, auth.isAdmin, isCAPManager, overallCapStatusValue, jobStatus);

  const factoryType = useMemo(() => {
    return (companyTypes.find(v => v.currentVersion.name === CompanyType.Factory) || {
      id: null,
    }).id;
  }, [companyTypes]);

  const companies = useMemo(() => (job.jobCompanies || []).filter(c => !!c.company), [job]);

  const factory = useMemo(() => companies.find(c => {
    if (c.companyTypeId) {
      const companyType = c.company.currentVersion.companyTypes.find(cc => cc.id === factoryType);
      return companyType && companyType.id === c.companyTypeId;
    }

    return c.company.currentVersion.companyTypes.map(cc => cc.id).includes(factoryType);
  }), [companies, factoryType]);

  const getStateProvinceName = async (id) => {
    if (!id) {
      return '';
    }
    try {
      const response = await client.query({
        query: STATE_PROVINCE_QUERY,
        variables: { id },
      });
      return response?.data?.stateProvince?.name || '';
    } catch (err) {
      return '';
    }
  };

  useEffect(() =>{
    setLoading(isCapSubmitting);
  }, [isCapSubmitting]);

  useEffect(() => {
    if (capFindingsLoading) {
      setIsDirty(false);
    }
  }, [capFindingsLoading]);

  useEffect(() => {
    const getData = async () => {
      const addresses = await Promise.all(
        (factory?.company?.currentVersion?.companyAddresses || [])
          .map(async companyAddress => {
            const { currentVersion } = companyAddress;
            const stateProvinceName = await getStateProvinceName(currentVersion.stateProvinceId);
            return compact([
              (currentVersion.address1 || ''),
              (currentVersion.address2 || ''),
              (currentVersion.city || ''),
              (stateProvinceName || ''),
              (currentVersion.country?.currentVersion?.name || ''),
            ]).join(', ');
          }));
      setFactoryAddresses(compact(addresses).join('.<br/>'));
    };
    getData();
  }, [factory]);
  
  useEffect(() => {
    if (!capFindingsLoading) {
      getCapHistories({
        variables: { findingIds: capFindingsData?.findings.map(f => f.id) },
      });
    }
    setLoading(capFindingsLoading);
  }, [capFindingsLoading, capFindingsData]);

  useEffect(() => {
    if (!isLoadingJobExternalStakeholders) {
      setTriggerRefetchJobExternalStakeholders((trigger) => trigger + 1);
    }
  }, [jobStatus]);

  useEffect(() => {
    if (jobChecklists &&
      Array.isArray(jobChecklists) &&
      jobChecklists.length > 0) {
      getChecklists(jobChecklists.map(({ currentVersion }) => currentVersion.id));
    }
  }, [jobChecklists.map(({ currentVersion }) => currentVersion.id).join(',')]);

  const findings = (capFindingsData && capFindingsData.findings) || [];
  
  useEffect(() => {
    if (!(capFindingsData && capFindingsData.findings && findings.length !== 0)) {     
      reloadCapFindings(jobId);
    }
  }, []);

  const sectionsWithNumbersFlatList = checklists.map(checklist =>
    sectionsWithNumbersFlat(
      flatToTree(
        checklist.currentVersion.checklistSections || [],
        null,
        SurveyType.Checklist)
    )).flat() || [];

  const findingSectionNumber = (findingSectionId) => sectionsWithNumbersFlatList
    .find(sec => sec.id === findingSectionId)?.sectionNumber;

  const questions = sectionsWithNumbersFlatList.map(sec => sec.questions.map(quest => quest)).flat();

  const findingQuestion = (findingChecklistQuestionId) => questions
    .find(quest => quest.checklistQuestionId === findingChecklistQuestionId);

  const findChecklistBySectionId = (findingSectionId) => checklists
    .find(checklist => checklist.currentVersion.checklistSections.find(sec => sec.id === findingSectionId));

  if (loading) {
    return <Loader />;
  }

  const b64toBlob = (base64, type = 'application/octet-stream') => {
    return fetch(`data:${type};base64,${base64}`).then(res => res.blob());
  };

  const getFindingsInfo = () => {
    return findings.map(finding => {
      const checklist = findChecklistBySectionId(finding.info?.sectionId);

      const localFindingSectionNumber = findingSectionNumber(finding.info?.sectionId);
      const localFindingQuestion = findingQuestion(finding.info?.checklistQuestionId);
      const questionNumber = localFindingSectionNumber && localFindingQuestion ? getQuestionNumber(localFindingSectionNumber, localFindingQuestion) : '';

      let findingLabel = '';
      if (finding.info?.section) {
        findingLabel = `${finding.info?.section} > `;
        if (questionNumber) {
          findingLabel += questionNumber;
        }
      }

      return {
        findingId: finding.id,
        path: findingLabel || '',
        checklistName: checklist?.currentVersion?.name || '',
        hasLegalRequirements: checklist?.currentVersion?.hasLegalRequirements || false,
        hasRecommendations: checklist?.currentVersion?.hasRecommendations || false,
      };
    });
  };

  const exportData = async (dataWithAttachments = false) => {
    try {
      setExportLoading(true);
      let pdf64 = null;
      try {
        const response = await client.mutate({
          mutation: EXPORT_CAP_NEW,
          variables: { jobId: job.id, findingsInfo: getFindingsInfo() },
        });
        pdf64 = response?.data?.exportCapNew?.pdfBase64;
      } catch (err) {
        logger.error('Export CAP error', err)
      }
      if (!pdf64) {
        setExportLoading(false);
        notification.error(t('FAILED_TO_EXPORT_CAP'));
        return;
      }
      const pdfBlob = await b64toBlob(pdf64, 'application/pdf');
      if (dataWithAttachments) {
        const attachmentsData = await Promise.all(findings.map(async (finding) => {
          const attachmentsMeta = finding?.actions?.map((action, actionIndex) => {
            if (action?.attachmentsMeta &&
              Array.isArray(action.attachmentsMeta) &&
              action.attachmentsMeta.length > 0) {
              return action.attachmentsMeta.map(attMeta => {
                const data = { ...attMeta };
                data.findingId = finding.id;
                data.actionId = action.id;
                data.actionReference = action.actionReference || actionIndex + 1;
                return data;
              });
            }
            return [];
          });
          return attachmentsMeta.flat();
        }));

        await downloadAttachments(attachmentsData.flat(), {
          zipFolderName: t('EVIDENCES'),
          zipFileName: `${t('CAP_EXPORT')}_${job.reportNumber}`,
          putExtra: true,
          extras: [
            {
              blobData: pdfBlob,
              name: `${t('CAP_EXPORT')}_${job.reportNumber}.pdf`,
            },
          ],
        });
        setExportLoading(false);
      } else {
        await saveAs(pdfBlob, `${t('CAP_EXPORT')}_${job.reportNumber}.pdf`);
        setExportLoading(false);
      }
    } catch (err) {
      logger.error('Caught', err);
    } finally {
      setExportLoading(false);
    }
  };

  const getOriginalFinding = (findingId) => {
    return findings.find(finding => finding.id === findingId) || null;
  };

  const wasCapSavedBefore = wasCapSaved(capFindingsData);

  const CAPInvitePopup = () => {
    return (
      <SiteRepresentativeInvitePopup
        jobId={jobId}
        jobExternalStakeholders={jobExternalStakeholders}
        isLoadingJobExternalStakeholders={isLoadingJobExternalStakeholders}
        onSubmit={() => setTriggerRefetchJobExternalStakeholders((trigger) => trigger + 1)}
        onClose={() => setShowInvitePopup(false)}
        auth={auth}
        job={job}
        companyTypes={companyTypes}
        serviceType={'cap'}
      />
    );
  }

  const CAPReviewerColumns = [
    {
      Header: `${t('COLUMN_NAME')}`,
      id: 'name',
      accessor: (row) => `${row.user.firstName} ${row.user.lastName}`,
      show: true,
      width: 100,
      disableSortBy: true,
    },
    {
      Header: `${t('COLUMN_EMAIL')}`,
      id: 'email',
      accessor: 'user.email',
      show: true,
      width: 200,
      disableSortBy: true,
    },
  ];
  
  return (
    <>
      {exportLoading && <s.Spinner><EiqSpinner /></s.Spinner>}
      <s.Wrapper id="cap-tab-content">
        {showInvitePopup && CAPInvitePopup()}
        <s.Left>
          <CapBoxPageHeader values={values} 
                            setLoading={setLoading}
                            reloadCapFindings={reloadCapFindings}
                            jobId={jobId}
                            setIsDirty={setIsDirty}
                            isEditable={isEditable}
                            errors={errors}                            
                            handleSubmit={handleSubmit}
                            hasError={hasError}
                            isCapManager={auth.isCapManager}
                            originalFindings={[...findings]}
                            isExternalUser={isExternalUser}
                            clientId={job?.clientId}
                            isAdmin={auth.isAdmin}
                            overallCapStatusValue={overallCapStatusValue}
                            isDirty={isDirty}
                            onExportClicked={() => exportData(true)}
                            wasCapSavedBefore={wasCapSavedBefore}
                            jobStatus={jobStatus}
          />
          <CapboxHeader site={
              {
                name: factory ? factory.company.currentVersion.name : '-',
                localName: factory ? factory.company.currentVersion.localName : '-',
                address: factoryAddresses,
                country: country ? country.currentVersion.name : '-',
                reportNumber: job.reportNumber
              }
            }
                        setFieldValue={setFieldValue}
                        setIsDirty={setIsDirty}
                        clientId={clientId}
                        setClientId={setClientId}
                        errors={errors}
                        disableClientId={disableClickForClient}
          />
          <CapBoxAssessmentInformation 
            auditStartAt={job?.auditStartAt}
            auditEndAt={job?.auditEndAt}
            jobServices={job?.jobServices}
          />
          <CapboxAssessmentResult findings={findings} />
          <CapboxDigitalLearning disableMyCourse={disableClickForCourse}/>
          <s.ResponsibleTableContainer>
            <s.Title>
              {t('RESPONSIBLE_TEAM')}
            </s.Title>
            <s.CapBoxTableWrapper>
              <SiteRepresentative setIsLoadingJobExternalStakeholders={setIsLoadingJobExternalStakeholders} 
                                  setJobExternalStakeholdersData={(data) => setJobExternalStakeholders(data)} 
                                  showInvitePopupWindow={() => setShowInvitePopup(true)} 
                                  jobId={jobId} serviceType={ServiceType} 
                                  triggerRefetchJobExternalStakeholders={triggerRefetchJobExternalStakeholders} 
                                  isEditable={isEditable}
                                  redirect={redirect}
                                  currentUserId={currentUserId} 
                                />
              <s.Separator></s.Separator>
              <s.CapManagerJobTable>
                <s.TableTitle>
                {t('CAP_REVIEWER')}
                </s.TableTitle>
                <Table
                  header={() => null}
                  gridColumns={CAPReviewerColumns}
                  data={jobStakeholders ? capManagerJobStakeholders : []}
                  hideDeletedButton={true}
                  showColumnSettings={false}
                  showPagination={false}
                />
              </s.CapManagerJobTable>
            </s.CapBoxTableWrapper>
          </s.ResponsibleTableContainer>

          <CapboxKeyMilestoneDates job={job}/>

          <CapboxProgress findings={findings} 
                          jobStatus={jobStatus} 
                          overallCapStatus={overallCapStatusValue}
                          capType={job?.capType}
          />
          {!loading && (
            // eslint-disable-next-line
            <s.CapSortingDiv>
              <s.FindingWrapper>
                <div className='pl-15'>
                  <s.CapManagmentTitle>{ t('CAP_IMPLEMENTATION_DETAILS') }</s.CapManagmentTitle>
                  {(values.findings || []).map((finding, index) => {
                    return (
                      <s.CapBoxDiv key={finding.id}>
                        <CapFinding
                          isCapManager={auth.isCapManager}
                          isAdmin={auth.isAdmin}
                          editableForExternalUser={editableForExternalUser}
                          finding={finding}
                          index={index}
                          setFieldValue={setFieldValue}
                          errors={errors?.findings && errors?.findings[index]}
                          findings={values.findings}
                          originalFinding={getOriginalFinding(finding.id)}
                          setIsDirty={setIsDirty}
                          currentUserId={currentUserId}
                          jobStatus={jobStatus}
                          overallCapStatus={job.overallCapStatus}
                          capType={job.capType}
                        /> 
                      </s.CapBoxDiv>
                    );
                  })}
                </div>
              </s.FindingWrapper>
            </s.CapSortingDiv>
          )}
        </s.Left>
        <s.Right className="ignore-in-pdf">
          <s.CollapsedBtn
            lang={i18n.language}
            onClick={() => {
              setInfoCollapsed(false);
            }}
          >
            {t('CAP_FEEDBACK_HISTORY')}
          </s.CollapsedBtn>
        </s.Right>
        <s.InfoOverlay hidden={infoCollapsed}>
          <s.FeedbackHistoryText>
            {t('CAP_FEEDBACK_HISTORY')}
          </s.FeedbackHistoryText>
          <s.CloseBtn name="cross" onClick={() => setInfoCollapsed(true)} />
          <s.HistoryDiv>
            <s.HistoryRow key={0}>
              <s.ActivityColumn title>{t('CAP_ACTIVITY')}</s.ActivityColumn>
              <s.IdColumn title>{t('CAP_ID')}</s.IdColumn>
              <s.SubmittedOnColumn title>
                {t('CAP_SUBMITTED_ON')}
              </s.SubmittedOnColumn>
              <s.SubmittedByColumn title>
                {t('CAP_SUBMITTED_BY')}
              </s.SubmittedByColumn>
            </s.HistoryRow>
            <s.HistoryLine />
            {!capHistoriesLoading &&
              capHistoriesData &&
              capHistoriesData.capHistories.map(history => {
                return (
                  <s.HistoryRow key={history.id}>
                    <s.ActivityColumn>{history.activity}</s.ActivityColumn>
                    <s.IdColumn>{history.checklistQuestionId}</s.IdColumn>
                    <s.SubmittedOnColumn>
                      {toSimpleUtcDateString(history.submittedAt)}
                    </s.SubmittedOnColumn>
                    <s.SubmittedByColumn>{`${history?.submittedByUser?.lastName || ''} ${history?.submittedByUser?.firstName || ''}`}</s.SubmittedByColumn>
                  </s.HistoryRow>
                );
              })}
          </s.HistoryDiv>
        </s.InfoOverlay>
      </s.Wrapper>
    </>
  );
};

CapBoxNew.propTypes = CapBoxPropTypes;
CapBoxNew.defaultProps = CapBoxDefaultProps;

export default React.memo(withJobContext(CapBoxNew));